import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  selectPartnerTerms,
  selectPartnerTermsAcceptStatus,
  selectPartnerTermsStatus,
} from "../features/referrals/referrals-slice";
import {
  acceptPartnerTerms,
  fetchPartnerTerms,
} from "../features/referrals/slice-extensions/referrals-slice-extension";
import { PARTNER_TERMS_DOCUMENT } from "../features/referrals/partner-terms-document";
import { LoadingStatus } from "../features/sessions/models/LoadingStatus";
import PartnerTermsAcceptedCard from "../components/referrals/PartnerTermsAcceptedCard";
import PartnerTermsAcceptanceForm from "../components/referrals/PartnerTermsAcceptanceForm";

/**
 * Public, tokenized page where a referral partner reads and accepts the partner
 * terms. Partners are not app users — the secret link token is their
 * credential. Once accepted, the page shows their referral code and share
 * link. English-only, like the other platform legal documents.
 */
const PartnerTermsPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const dispatch = useAppDispatch();
  const terms = useAppSelector(selectPartnerTerms);
  const status = useAppSelector(selectPartnerTermsStatus);
  const acceptStatus = useAppSelector(selectPartnerTermsAcceptStatus);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (token) {
      dispatch(fetchPartnerTerms(token));
    }
  }, [dispatch, token]);

  if (!token || status === LoadingStatus.Failed) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <p className="text-grey-30 dark:text-grey-70">
          This partner link is not valid. Please contact us if you believe this is a mistake.
        </p>
      </div>
    );
  }

  if (!terms) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <p className="text-grey-30 dark:text-grey-70">Loading…</p>
      </div>
    );
  }

  const isSubmitting = acceptStatus === LoadingStatus.Loading;

  const effectiveDate = new Date(PARTNER_TERMS_DOCUMENT.effectiveDate).toLocaleDateString(
    undefined,
    { year: "numeric", month: "long", day: "numeric" }
  );

  return (
    <article className="mx-auto max-w-3xl px-4 py-8">
      <h1 className="mb-2 text-3xl font-bold text-grey-10 dark:text-white">
        {PARTNER_TERMS_DOCUMENT.title}
      </h1>
      <p className="mb-4 text-sm text-grey-50 dark:text-grey-70">
        For {terms.partnerName} · Version {PARTNER_TERMS_DOCUMENT.version} · Effective{" "}
        {effectiveDate}
      </p>
      <p className="mb-8 leading-relaxed text-grey-20 dark:text-grey-80">
        {PARTNER_TERMS_DOCUMENT.summary}
      </p>

      {terms.accepted && (
        <PartnerTermsAcceptedCard code={terms.code} acceptedAt={terms.acceptedAt} />
      )}

      <div className="space-y-6">
        {PARTNER_TERMS_DOCUMENT.sections.map((section) => (
          <section key={section.heading}>
            <h2 className="mb-2 text-xl font-semibold text-grey-10 dark:text-white">
              {section.heading}
            </h2>
            {section.body.map((paragraph, index) => (
              <p key={index} className="mb-3 leading-relaxed text-grey-30 dark:text-grey-70">
                {paragraph}
              </p>
            ))}
          </section>
        ))}
      </div>

      {!terms.accepted && (
        <PartnerTermsAcceptanceForm
          partnerName={terms.partnerName}
          termsVersion={terms.currentTermsVersion}
          agreed={agreed}
          onAgreedChange={setAgreed}
          onAccept={() => dispatch(acceptPartnerTerms(token))}
          isSubmitting={isSubmitting}
        />
      )}
    </article>
  );
};

export default PartnerTermsPage;
